import { Link } from "@tanstack/react-router";

import { BlogCover } from "./blog-cover";
import { Byline } from "./byline";
import { PostMeta } from "./post-meta";
import type { BlogPostCard } from "../types";
import { Eyebrow } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-page-background";

/** The shelf a post sits on, as a frosted label over its picture. */
const ShelfLabel = ({ name }: { name: string }) => (
  <span className="absolute top-3 left-3 rounded-full border border-background/70 bg-background/80 px-3 py-1 text-xs font-medium backdrop-blur-md">
    {name}
  </span>
);

/** One post in a grid: its picture, date and reading time, title, standfirst and byline. */
export const PostCard = ({ post, priority }: { post: BlogPostCard; priority?: boolean }) => (
  <Link
    to="/blog/$slug"
    params={{ slug: post.slug }}
    lang={post.locale}
    className={cn("group flex flex-col gap-4 rounded-[28px]", focusRing)}
  >
    <div className="relative overflow-hidden rounded-[24px] border border-border">
      <BlogCover
        src={post.coverUrl}
        alt={post.coverAlt}
        width={post.coverWidth}
        height={post.coverHeight}
        priority={priority}
        className="aspect-[4/3] w-full transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.03]"
      />
      {post.category ? <ShelfLabel name={post.category.name} /> : null}
    </div>
    <div className="flex flex-1 flex-col gap-2.5 px-1">
      <PostMeta publishedAt={post.publishedAt} readingMinutes={post.readingMinutes} />
      <h3 className="text-[1.375rem] leading-[1.15] font-medium tracking-[-0.025em] text-balance transition-colors group-hover:text-[var(--site-ink)]">
        {post.title}
      </h3>
      {post.excerpt ? <p className="line-clamp-2 text-[15px] leading-relaxed text-muted-foreground">{post.excerpt}</p> : null}
      <Byline authors={post.authors} className="mt-auto pt-2" />
    </div>
  </Link>
);

/** The story on top of the front page: picture on one side, a big title and the standfirst on the other. */
export const FeaturedPostCard = ({ post }: { post: BlogPostCard }) => (
  <Link
    to="/blog/$slug"
    params={{ slug: post.slug }}
    lang={post.locale}
    className={cn(
      "group grid grid-cols-[minmax(0,1fr)] overflow-hidden rounded-[32px] border border-border bg-background p-2.5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]",
      focusRing,
    )}
  >
    {/* On a wide screen the words decide the card's height and the picture fills its side, whatever its shape. */}
    <div className="relative aspect-[16/11] overflow-hidden rounded-[24px] lg:aspect-auto lg:min-h-[460px]">
      <BlogCover
        src={post.coverUrl}
        alt={post.coverAlt}
        width={post.coverWidth}
        height={post.coverHeight}
        priority
        className="absolute inset-0 size-full transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.02]"
      />
    </div>
    <div className="flex flex-col gap-5 px-4 pt-7 pb-5 md:px-8 lg:py-10">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Eyebrow>{m.blog_featured()}</Eyebrow>
        {post.category ? <span className="text-xs font-medium text-muted-foreground">{post.category.name}</span> : null}
      </div>
      <PostMeta publishedAt={post.publishedAt} readingMinutes={post.readingMinutes} />
      <h2 className="text-[clamp(1.875rem,3.4vw,3.25rem)] leading-[1.02] font-medium tracking-[-0.045em] text-balance transition-colors group-hover:text-[var(--site-ink)]">
        {post.title}
      </h2>
      {post.excerpt ? (
        <p className="max-w-[46ch] text-[17px] leading-relaxed text-pretty text-muted-foreground">{post.excerpt}</p>
      ) : null}
      <Byline authors={post.authors} detailed className="mt-auto pt-4" />
    </div>
  </Link>
);
